export type SortBy = 'recent' | 'votes' | 'comments';

export interface MediaItem {
  id: string;
  public_id: string;
  secure_url: string;
  resource_type: 'image' | 'video';
  format: string;
  width: number;
  height: number;
  created_at?: string;
  uploaded_by?: string;
  user_name?: string;
  user_avatar?: string;
  description?: string;
  votes_count: number;
  comments_count: number;
  cdn_thumb?: string;
  cdn_display_s?: string;
  cdn_display_m?: string;
  cdn_original?: string;
  colors?: [string, number][];
  flagged?: boolean;
  pending?: boolean;
}

export interface UserOption {
  id: string;
  name: string;
  avatar: string | null;
}

export type UserLookup = Record<string, { name: string; avatar: string | null }>;
type DbRow = Record<string, unknown>;
type DbUser = Record<string, unknown>;

const asString = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : fallback;

const asNumber = (value: unknown, fallback = 0): number =>
  typeof value === 'number' ? value : fallback;

export const findAssetIndexByFotoId = (assets: MediaItem[], fotoId: string): number => {
  return assets.findIndex((asset) => {
    const publicId = asset.secure_url.split('/').pop()?.split('.')[0];
    return publicId === fotoId || asset.public_id === fotoId;
  });
};

export const filterAndSortAssets = (
  assets: MediaItem[],
  selectedUsers: Set<string>,
  sortBy: SortBy,
  hideMyPhotos = false,
  currentUserId?: string | null,
): MediaItem[] => {
  let filtered = assets;

  if (hideMyPhotos && currentUserId) {
    filtered = filtered.filter((asset) => asset.uploaded_by !== currentUserId);
  }

  if (selectedUsers.size > 0) {
    filtered = filtered.filter((asset) => asset.uploaded_by && selectedUsers.has(asset.uploaded_by));
  }

  return [...filtered].sort((a, b) => {
    if (sortBy === 'recent') {
      const dateA = new Date(a.created_at || '').getTime();
      const dateB = new Date(b.created_at || '').getTime();
      return dateB - dateA;
    }

    if (sortBy === 'comments') {
      return (b.comments_count || 0) - (a.comments_count || 0);
    }

    return b.votes_count - a.votes_count;
  });
};

export const buildLoopedAssets = (assets: MediaItem[]): MediaItem[] => {
  if (assets.length === 0) return [];
  return [assets[assets.length - 1], ...assets, assets[0]];
};

export const buildGridColumns = (assetCount: number, columnsCount: number): number[][] => {
  const columns: number[][] = Array.from({ length: columnsCount }, () => []);
  for (let i = 0; i < assetCount; i += 1) {
    columns[i % columnsCount].push(i);
  }
  return columns;
};

export const getUniqueUsersFromAssets = (assets: MediaItem[]): UserOption[] => {
  const userMap = new Map<string, UserOption>();
  assets.forEach((asset) => {
    if (asset.uploaded_by && asset.user_name) {
      userMap.set(asset.uploaded_by, {
        id: asset.uploaded_by,
        name: asset.user_name,
        avatar: asset.user_avatar || null,
      });
    }
  });
  return Array.from(userMap.values());
};

export const createUserLookup = (usersData: DbUser[]): UserLookup => {
  return usersData.reduce((acc, user) => {
    const id = asString(user.id);
    if (!id) return acc;
    acc[id] = {
      name: asString(user.display_name, 'Un invitado'),
      avatar: asString(user.avatar_url) || null,
    };
    return acc;
  }, {} as UserLookup);
};

export const mapSupabaseRowsToMediaItems = (rows: DbRow[], userLookup: UserLookup): MediaItem[] => {
  return rows
    .filter((row) => {
      const uploadedBy = asString(row.uploaded_by);
      return !!uploadedBy && !!userLookup[uploadedBy];
    })
    .map((row) => {
      const uploadedBy = asString(row.uploaded_by);
      const resourceType = asString(row.resource_type, 'image') === 'video' ? 'video' : 'image';
      return {
      id: asString(row.id),
      public_id: asString(row.public_id),
      secure_url: asString(row.secure_url),
      resource_type: resourceType,
      format: '',
      width: asNumber(row.width),
      height: asNumber(row.height),
      created_at: asString(row.created_at) || asString(row.inserted_at) || undefined,
      uploaded_by: uploadedBy,
      user_name: userLookup[uploadedBy]?.name || 'Un invitado',
      user_avatar: userLookup[uploadedBy]?.avatar || null,
      description: asString(row.description) || undefined,
      votes_count: asNumber(row.votes_count),
      comments_count: asNumber(row.comments_count),
      cdn_thumb: asString(row.cdn_thumb) || undefined,
      cdn_display_s: asString(row.cdn_display_s) || undefined,
      cdn_display_m: asString(row.cdn_display_m) || undefined,
      cdn_original: asString(row.cdn_original) || undefined,
      colors: (Array.isArray(row.colors) ? row.colors : null) as [string, number][] | null,
      flagged: Boolean(row.flagged),
      pending: Boolean(row.pending),
    }});
};

export const filterRenderableMediaItems = (items: MediaItem[]): MediaItem[] => {
  return items.filter(
    (item) =>
      item &&
      !item.pending &&
      (item.secure_url && item.resource_type && item.width && item.height) &&
      (item.created_at || item.uploaded_by),
  );
};

export const sortByCreationDateDesc = (items: MediaItem[]): MediaItem[] => {
  return [...items].sort((a, b) => {
    if (!a.created_at || !b.created_at) return 0;
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });
};
